import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Calendar, ChevronLeft, ChevronRight, Eye, X, Download, Filter } from 'lucide-react';
import { ownerApi } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const AgreementsPanel = () => {
  const [data, setData] = useState({ agreements: [], total: 0, page: 1, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ creator: '', content: '', date_from: '', date_to: '' });
  const [activeFilters, setActiveFilters] = useState({});
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ownerApi.getAllAgreements({ ...activeFilters, page, limit: 20 });
      setData(result);
    } catch (e) {
      console.error('Failed to load agreements:', e);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, page]);

  useEffect(() => { fetchAgreements(); }, [fetchAgreements]);

  const applyFilters = () => {
    const f = {};
    if (filters.creator.trim()) f.creator = filters.creator.trim();
    if (filters.content.trim()) f.content = filters.content.trim();
    if (filters.date_from) f.date_from = filters.date_from;
    if (filters.date_to) f.date_to = filters.date_to;
    setActiveFilters(f);
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ creator: '', content: '', date_from: '', date_to: '' });
    setActiveFilters({});
    setPage(1);
  };

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="agreements-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#8B5CF6]" /> Creator Agreement Records
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Immutable audit log of all creator agreement acceptances ({data.total} total)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1.5"
          data-testid="agreements-toggle-filters"
        >
          <Filter className="w-3.5 h-3.5" />
          {showFilters ? 'Hide Filters' : 'Filters'}
          {hasActiveFilters && (
            <span className="ml-1 w-4 h-4 text-[10px] bg-[#8B5CF6] text-white rounded-full flex items-center justify-center">
              {Object.keys(activeFilters).length}
            </span>
          )}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="admin-card p-4 space-y-3" data-testid="agreements-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Creator</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <Input
                  placeholder="Name, email or ID"
                  value={filters.creator}
                  onChange={e => setFilters(f => ({ ...f, creator: e.target.value }))}
                  className="pl-8 h-9 text-sm bg-[#0F0B1E] border-white/10"
                  data-testid="agreements-filter-creator"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Content</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <Input
                  placeholder="Title or content ID"
                  value={filters.content}
                  onChange={e => setFilters(f => ({ ...f, content: e.target.value }))}
                  className="pl-8 h-9 text-sm bg-[#0F0B1E] border-white/10"
                  data-testid="agreements-filter-content"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">From Date</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="pl-8 h-9 text-sm bg-[#0F0B1E] border-white/10"
                  data-testid="agreements-filter-date-from"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">To Date</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="pl-8 h-9 text-sm bg-[#0F0B1E] border-white/10"
                  data-testid="agreements-filter-date-to"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={applyFilters} data-testid="agreements-apply-filters">
              Apply Filters
            </Button>
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="text-gray-400">
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {loading && !data.agreements.length ? (
        <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
      ) : data.agreements.length === 0 ? (
        <div className="admin-card p-10 text-center">
          <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">
            {hasActiveFilters ? 'No agreements match your filters' : 'No agreement records yet'}
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Records are created when creators accept the agreement during content submission
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="admin-card overflow-x-auto hidden md:block" data-testid="agreements-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-gray-400 text-xs">
                  <th className="text-left py-3 px-4 font-medium">Creator</th>
                  <th className="text-left py-3 px-4 font-medium">Content</th>
                  <th className="text-left py-3 px-4 font-medium">Date</th>
                  <th className="text-left py-3 px-4 font-medium">IP Address</th>
                  <th className="text-left py-3 px-4 font-medium">Version</th>
                  <th className="text-center py-3 px-4 font-medium">Agreement</th>
                </tr>
              </thead>
              <tbody>
                {data.agreements.map((a) => (
                  <tr key={a.acceptance_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-4">
                      <p className="text-gray-200 font-medium text-sm">{a.company_name || '—'}</p>
                      <p className="text-gray-500 text-xs">{a.creator_email || a.user_id}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-gray-300 text-sm">{a.content_title || '—'}</p>
                      <p className="text-gray-600 text-xs">{a.content_type || ''} {a.content_id ? `· ${a.content_id}` : ''}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-xs whitespace-nowrap">
                      {formatDate(a.accepted_at)}
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-xs font-mono">
                      {a.ip_address || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] px-2 py-0.5 rounded">
                        v{a.agreement_version || '?'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setExpanded(expanded === a.acceptance_id ? null : a.acceptance_id)}
                        className="p-1.5 rounded hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                        data-testid={`view-agreement-${a.acceptance_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="agreements-mobile-list">
            {data.agreements.map((a) => (
              <div key={a.acceptance_id} className="admin-card p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-200">{a.company_name || '—'}</p>
                    <p className="text-xs text-gray-500">{a.creator_email || a.user_id}</p>
                  </div>
                  <span className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] px-2 py-0.5 rounded">
                    v{a.agreement_version || '?'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{a.content_title || 'No content'}</span>
                  <span className="font-mono">{a.ip_address || '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{formatDate(a.accepted_at)}</span>
                  <button
                    onClick={() => setExpanded(expanded === a.acceptance_id ? null : a.acceptance_id)}
                    className="text-xs text-[#8B5CF6] hover:underline flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" /> View Text
                  </button>
                </div>
                {expanded === a.acceptance_id && (
                  <div className="mt-2 p-3 bg-black/30 rounded-lg border border-white/5 max-h-60 overflow-y-auto">
                    <div className="text-xs text-gray-300 prose prose-invert prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: a.agreement_text || '<p>Agreement text not available</p>' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between" data-testid="agreements-pagination">
              <p className="text-xs text-gray-500">
                Page {data.page} of {data.total_pages} ({data.total} records)
              </p>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="agreements-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= data.total_pages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="agreements-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Expanded agreement text modal (desktop) */}
      {expanded && (
        <div className="hidden md:block">
          {data.agreements.filter(a => a.acceptance_id === expanded).map(a => (
            <div key={a.acceptance_id} className="admin-card p-5 space-y-3" data-testid="agreement-detail-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#8B5CF6]" />
                  Agreement Text — {a.company_name} — {formatDate(a.accepted_at)}
                </h3>
                <button
                  onClick={() => setExpanded(null)}
                  className="p-1 rounded hover:bg-white/5 text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-black/30 rounded-lg border border-white/5 p-5 max-h-96 overflow-y-auto">
                <div className="prose prose-invert prose-sm max-w-none text-gray-300"
                  dangerouslySetInnerHTML={{ __html: a.agreement_text || '<p>Agreement text not available for this record</p>' }}
                />
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-2 border-t border-white/5">
                <span>ID: {a.acceptance_id}</span>
                <span>Creator: {a.creator_email || a.user_id}</span>
                <span>Content: {a.content_title || a.content_id || '—'}</span>
                <span>IP: {a.ip_address || '—'}</span>
                <span>Version: v{a.agreement_version}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgreementsPanel;
